import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@tms/shared';

import { DepartmentFilter } from '@/components/DepartmentSelect';
import { api } from '@/api/client';
import { useApiMutation, useDebouncedValue, usePaged } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { createdAtColumn } from '@/components/createdAtColumn';
import { activeParam, listParam, StatusFilter } from '@/components/StatusFilter';
import { StickyTable } from '@/components/StickyTable';
import { TableCard } from '@/components/TableCard';

import { EmployeeDrawer } from './employees/EmployeeDrawer';
import { WORK_SCHEDULE_LABEL } from './employees/MainSection';

interface EmployeeRow {
  id: number;
  personnelNumber: string;
  lastName: string;
  firstName: string;
  middleName: string | null;
  phone: string | null;
  workSchedule: string | null;
  birthDate?: string;
  isActive: boolean;
  createdAt: string;
  department: { id: number; name: string } | null;
  position: { id: number; name: string } | null;
}

export function EmployeesPage() {
  const { can } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const canUpdate = can(PERMISSIONS.EMPLOYEE_UPDATE);
  const canCreate = can(PERMISSIONS.EMPLOYEE_CREATE);
  const canDelete = can(PERMISSIONS.EMPLOYEE_DELETE);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);
  const [departmentId, setDepartmentId] = useState<number[]>([]);
  const [activity, setActivity] = useState<Array<'active' | 'dismissed'>>([]);
  const isActive = activeParam(activity);

  /** Открытая карточка: id, 'new' — приём, null — закрыта. */
  const [cardId, setCardId] = useState<number | 'new' | null>(null);

  const query = usePaged<EmployeeRow>(['employees'], '/employees', {
    page,
    pageSize,
    search: search || undefined,
    departmentId: listParam(departmentId),
    isActive,
  });

  // Открытие карточки по ссылке из «Водителей» (?focus=id) — независимо
  // от текущего фильтра и страницы списка.
  const focusId = Number(searchParams.get('focus')) || undefined;
  useEffect(() => {
    if (!focusId) return;
    setCardId(focusId);
    setSearchParams((params) => {
      params.delete('focus');
      return params;
    });
  }, [focusId, setSearchParams]);

  const remove = useApiMutation(
    async (id: number) => (await api.delete(`/employees/${id}`)).data,
    { successMessage: t('Сотрудник удалён'), invalidate: [['employees'], ['office-summary']] },
  );

  if (!can(PERMISSIONS.EMPLOYEE_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр сотрудников')}</Typography.Text>;
  }

  return (
    <TableCard
      title={t('Сотрудники')}
      extra={
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={t('Фамилия или табельный номер')}
            style={{ width: 260 }}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(1);
            }}
          />
          <DepartmentFilter
            value={departmentId}
            onChange={(value) => {
              setDepartmentId(value);
              setPage(1);
            }}
          />
          <StatusFilter
            width={170}
            value={activity}
            onChange={(value) => {
              setActivity(value);
              setPage(1);
            }}
            options={[
              { value: 'active', label: t('Работает'), color: 'green' },
              { value: 'dismissed', label: t('Уволен'), color: 'default' },
            ]}
          />
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCardId('new')}
            >
              {t('Принять сотрудника')}
            </Button>
          )}
        </Space>
      }
    >
      <StickyTable<EmployeeRow>
        rowKey="id"
        exportSource={{
          url: '/employees',
          params: { search: search || undefined, departmentId: listParam(departmentId), isActive },
        }}
        rowNumbers
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        // Карточку можно открыть и без права правки — разделы покажутся
        // только для чтения.
        onRow={(row) => ({
          onClick: () => setCardId(row.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${t('Всего:')} ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        columns={[
          { title: t('Табельный'), dataIndex: 'personnelNumber', width: 120 },
          {
            title: t('ФИО'),
            render: (_: unknown, row: EmployeeRow) =>
              `${row.lastName} ${row.firstName} ${row.middleName ?? ''}`.trim(),
          },
          {
            title: t('Подразделение'),
            dataIndex: 'department',
            render: (department: EmployeeRow['department']) => department?.name ?? '—',
          },
          {
            title: t('Должность'),
            render: (_: unknown, row: EmployeeRow) => row.position?.name ?? '—',
          },
          {
            title: t('Дата рождения'),
            dataIndex: 'birthDate',
            width: 130,
            render: (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY') : '—'),
          },
          { title: t('Телефон'), dataIndex: 'phone', width: 160 },
          {
            title: t('Режим работы'),
            dataIndex: 'workSchedule',
            width: 150,
            render: (value: string | null) => (value ? t(WORK_SCHEDULE_LABEL[value] ?? value) : '—'),
          },
          {
            title: t('Статус'),
            dataIndex: 'isActive',
            width: 110,
            render: (active: boolean) => (
              <Tag color={active ? 'green' : 'default'}>{active ? t('Работает') : t('Уволен')}</Tag>
            ),
          },
          createdAtColumn<EmployeeRow>(t),
          {
            title: '',
            width: 90,
            render: (_: unknown, row: EmployeeRow) => (
              <Space size={0} onClick={(event) => event.stopPropagation()}>
                {canUpdate && (
                  <Tooltip title={t('Изменить')}>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setCardId(row.id)}
                    />
                  </Tooltip>
                )}
                {canDelete && (
                  <Popconfirm
                    title={t('Уволить сотрудника?')}
                    okText={t('Уволить')}
                    cancelText={t('Отмена')}
                    onConfirm={() => remove.mutate(row.id)}
                  >
                    <Tooltip title={t('Уволить')}>
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <EmployeeDrawer employeeId={cardId} onClose={() => setCardId(null)} />
    </TableCard>
  );
}
